export const SHADOW_OFFSET = 0.06
export const DEFAULT_BG = '#777777'
export const DEFAULT_TITLE_COLOR = '#FFFFFF'
export const FALLBACK_SHADOW = 'rgba(0, 0, 0, 0.1)'

type TRgb = { r: number; g: number; b: number }

const hexToRgb = (hex: string): TRgb | null => {
  const match = hex.trim().match(/^#?([a-f\d]{6})$/i)
  if (!match) return null
  const int = parseInt(match[1], 16)
  return { r: (int >> 16) & 255, g: (int >> 8) & 255, b: int & 255 }
}

const clamp255 = (n: number) => Math.max(0, Math.min(255, Math.round(n)))

const rgbToHex = (r: number, g: number, b: number) =>
  '#' + [r, g, b].map((c) => clamp255(c).toString(16).padStart(2, '0')).join('')

const darken = (hex: string, amount: number): string => {
  const rgb = hexToRgb(hex)
  if (!rgb) return hex
  return rgbToHex(rgb.r * (1 - amount), rgb.g * (1 - amount), rgb.b * (1 - amount))
}

/** Тон обложки: по нему плашка автора берёт светлый или тёмный текст. */
export type TCoverTone = 'light' | 'dark'

/** Выше этой яркости (relative luminance по W3C) фон считаем светлым */
const LIGHT_TONE_LUMINANCE = 0.5

const channelLuminance = (value: number) => {
  const channel = value / 255
  return channel <= 0.03928 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4
}

const relativeLuminance = ({ r, g, b }: TRgb) =>
  0.2126 * channelLuminance(r) + 0.7152 * channelLuminance(g) + 0.0722 * channelLuminance(b)

/**
 * Светлая обложка или тёмная — по средней яркости цветов градиента. Цвет
 * непонятен (пусто или не hex) — считаем тёмной: фон по умолчанию серый.
 */
export const getCoverTone = (gradient: string | null): TCoverTone => {
  const hexes = (gradient ?? DEFAULT_BG).match(/#[0-9a-fA-F]{6}/g)
  if (!hexes) return 'dark'

  const colors = hexes.map(hexToRgb).filter((rgb): rgb is TRgb => rgb !== null)
  if (colors.length === 0) return 'dark'

  const average = colors.reduce((sum, rgb) => sum + relativeLuminance(rgb), 0) / colors.length

  return average > LIGHT_TONE_LUMINANCE ? 'light' : 'dark'
}

export const getShadowColor = (gradient: string | null): string => {
  if (!gradient) return FALLBACK_SHADOW
  const hexes = gradient.match(/#[0-9a-fA-F]{6}/g)
  if (!hexes || hexes.length === 0) return FALLBACK_SHADOW
  return darken(hexes[0], 0.1)
}

/**
 * Основной цвет обложки — первый цвет её градиента. Без градиента — серый
 * фон по умолчанию.
 */
export const getCoverBaseColor = (gradient: string | null): string =>
  gradient?.match(/#[0-9a-fA-F]{6}/)?.[0] ?? DEFAULT_BG
